import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import accountsApi from '../../services/accountsApi';
import toast from 'react-hot-toast';

const todayISO = () => new Date().toISOString().slice(0, 10);

const SectionTable = ({ title, section, onAccountClick }) => (
  <div className="bg-white rounded-xl shadow overflow-hidden mb-4">
    <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 flex justify-between items-center">
      <h3 className="text-sm font-semibold text-gray-700">{title}</h3>
      <span className="text-sm font-semibold text-gray-800">{section.total.toFixed(2)}</span>
    </div>
    {section.groups.length === 0 ? (
      <div className="text-center py-6 text-gray-400 text-sm">No activity</div>
    ) : (
      <table className="w-full text-sm">
        <tbody>
          {section.groups.map(group => (
            <React.Fragment key={group.classification}>
              <tr className="bg-gray-50">
                <td colSpan={2} className="px-4 py-1.5 text-xs font-semibold text-gray-500">{group.classification}</td>
              </tr>
              {group.accounts.map(a => (
                <tr key={a.id} onClick={() => onAccountClick(a.id)} className="border-t border-gray-50 hover:bg-gray-50 cursor-pointer">
                  <td className="px-4 py-1.5 text-gray-700">{a.account_code} — {a.account_name}</td>
                  <td className="px-4 py-1.5 text-right text-gray-800">{a.balance.toFixed(2)}</td>
                </tr>
              ))}
              <tr className="border-t border-gray-100">
                <td className="px-4 py-1.5 text-right text-xs text-gray-500">Subtotal</td>
                <td className="px-4 py-1.5 text-right font-medium text-gray-800">{group.subtotal.toFixed(2)}</td>
              </tr>
            </React.Fragment>
          ))}
        </tbody>
      </table>
    )}
  </div>
);

const FinancialPosition = () => {
  const navigate = useNavigate();
  const [funds, setFunds] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [asOfDate, setAsOfDate] = useState(todayISO());
  const [fundId, setFundId] = useState('');
  const [costCenterId, setCostCenterId] = useState('');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    const loadRefs = async () => {
      try {
        const [fundsRes, ccRes] = await Promise.all([
          accountsApi.get('/funds'),
          accountsApi.get('/cost-centers', { params: { is_active: true } }),
        ]);
        setFunds(fundsRes.data.funds);
        setCostCenters(ccRes.data.cost_centers);
      } catch (err) {
        toast.error('Failed to load filter options');
      }
    };
    loadRefs();
  }, []);

  const runReport = async () => {
    try {
      setLoading(true);
      const params = {};
      if (asOfDate) params.as_of_date = asOfDate;
      if (fundId) params.fund_id = fundId;
      if (costCenterId) params.cost_center_id = costCenterId;
      const response = await accountsApi.get('/financial-statements/position', { params });
      setData(response.data.statement_of_financial_position);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load statement');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { runReport(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onAccountClick = (accountId) => navigate(`/ledgers?account_id=${accountId}&to_date=${asOfDate}`);

  return (
    <Layout title="Statement of Financial Position">
      <div className="bg-white rounded-xl shadow p-4 mb-4">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">As Of</label>
            <input type="date" value={asOfDate} onChange={(e) => setAsOfDate(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Fund</label>
            <select value={fundId} onChange={(e) => setFundId(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="">All Funds</option>
              {funds.map(f => <option key={f.id} value={f.id}>{f.code} — {f.name}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Cost Center</label>
            <select value={costCenterId} onChange={(e) => setCostCenterId(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
              <option value="">All Cost Centers</option>
              {costCenters.map(cc => <option key={cc.id} value={cc.id}>{cc.code} — {cc.name}</option>)}
            </select>
          </div>
          <button onClick={runReport} className="bg-blue-800 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-900">
            Apply
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8 text-gray-500">Loading...</div>
      ) : !data ? null : (
        <>
          <div className={`rounded-xl p-4 mb-4 flex items-center justify-between ${data.validation.is_balanced ? 'bg-green-50 border border-green-200' : 'bg-red-50 border border-red-200'}`}>
            <div>
              <div className={`font-semibold ${data.validation.is_balanced ? 'text-green-700' : 'text-red-700'}`}>
                {data.validation.status === 'BALANCED' ? '✓ Balanced' : '✗ Not Balanced'}
              </div>
              <div className="text-xs text-gray-500 mt-0.5">{data.validation.equation}</div>
            </div>
            {!data.validation.is_balanced && (
              <div className="text-red-700 text-sm font-medium">Difference: {data.validation.difference.toFixed(2)}</div>
            )}
          </div>

          {data.warnings?.length > 0 && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800 mb-4 space-y-1">
              {data.warnings.map((w, i) => <div key={i}>{w}</div>)}
            </div>
          )}

          <SectionTable title="Assets" section={data.sections.assets} onAccountClick={onAccountClick} />
          <SectionTable title="Liabilities" section={data.sections.liabilities} onAccountClick={onAccountClick} />
          <SectionTable title="Equity / Fund Balance" section={data.sections.net_assets.recorded_equity_fund} onAccountClick={onAccountClick} />

          <div className="bg-white rounded-xl shadow p-4 mb-4 text-sm space-y-2">
            <div className="flex justify-between"><span className="text-gray-600">Current Period Surplus / (Deficit)</span><span className="font-medium text-gray-800">{data.sections.net_assets.current_period_surplus_deficit.toFixed(2)}</span></div>
            <div className="flex justify-between border-t border-gray-100 pt-2"><span className="font-semibold text-gray-700">Total Net Assets</span><span className="font-semibold text-gray-800">{data.sections.net_assets.total.toFixed(2)}</span></div>
          </div>

          <div className="bg-white rounded-xl shadow p-4 text-sm space-y-2">
            <div className="flex justify-between"><span className="text-gray-600">Total Assets</span><span className="font-medium text-gray-800">{data.total_assets.toFixed(2)}</span></div>
            <div className="flex justify-between"><span className="text-gray-600">Total Liabilities + Net Assets</span><span className="font-medium text-gray-800">{data.total_liabilities_and_net_assets.toFixed(2)}</span></div>
          </div>
        </>
      )}
    </Layout>
  );
};

export default FinancialPosition;
